import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Search } from 'lucide-react';
import { API, getAuthHeaders } from '../../utils/adminAuth';

export default function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/admin/audit-logs`, { headers: getAuthHeaders() })
      .then(r => { if (!cancel) { setLogs(r.data || []); setLoading(false); } })
      .catch(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const actionColors = {
    page_edit: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
    product_update: 'text-purple-400 bg-purple-400/10 border-purple-400/30',
    product_delete: 'text-red-400 bg-red-400/10 border-red-400/30',
    order_update: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
    settings_update: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    login: 'text-green-400 bg-green-400/10 border-green-400/30',
    team_edit: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
  };

  const filtered = filter ? logs.filter(l =>
    (l.action || '').toLowerCase().includes(filter.toLowerCase()) ||
    (l.admin_name || '').toLowerCase().includes(filter.toLowerCase()) ||
    (l.details || '').toLowerCase().includes(filter.toLowerCase()) ||
    (l.target || '').toLowerCase().includes(filter.toLowerCase())
  ) : logs;

  return (
    <div data-testid="audit-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Audit Log</h2>
          <p className="text-white/40 text-xs font-sans mt-1">Last 200 admin actions, newest first.</p>
        </div>
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-white/30" />
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter by action, admin, target…"
            className="bg-[#141414] border border-white/10 px-3 py-1.5 text-xs text-white font-sans focus:border-[#1C7D3F] outline-none w-64"
            data-testid="audit-filter-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="bg-[#141414] border border-white/5 p-12 text-center">
          <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#141414] border border-white/5 p-12 text-center">
          <p className="text-white/30 text-sm font-sans">No audit logs match your filter.</p>
        </div>
      ) : (
        <div className="bg-[#141414] border border-white/5 divide-y divide-white/5" data-testid="audit-tab-list">
          {filtered.map((log, idx) => (
            <div key={idx} className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.02]">
              <span className={`text-[10px] uppercase font-mono tracking-wider px-2 py-0.5 border ${actionColors[log.action] || 'text-white/50 bg-white/5 border-white/10'}`}>
                {(log.action || '').replace(/_/g, ' ')}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white/80 text-sm font-sans">{log.details || log.target || '—'}</p>
                <p className="text-white/40 text-[11px] font-sans mt-0.5">
                  <span className="text-white/60">{log.admin_name || 'Admin'}</span>
                  {log.admin_email && <span className="text-white/30"> ({log.admin_email})</span>}
                  <span className="text-white/30"> &middot; </span>
                  {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
